/**
 * @module app.cm
 */

import { bindable } from 'aurelia-typed-observable-plugin';
import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { MemberService } from 'app/cd/services/member-service';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class MemberLabs extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    public memberLabs: ReadonlyArray<CD.LabResult> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected openProviderSummary = openProviderSummary;
    protected showProviderSummaryLink: boolean = false;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'testDate',
                direction: SortDirection.Descending
            }
        ];
    }

    protected bind(): void {
        this.checkPermissions();
        this.loadCustomFieldTargets();
        this.loadMemberLabs();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadMemberLabs();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ***********************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    ////****************** Service Calls ********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.LabResult');
    }

    @apiCall
    private async loadMemberLabs(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes: string[] = [
            'provider',
            'provider.location',
            'source',
            'test'
        ];

        const fields = [
            'custom.lookup.*',
            'custom.text.*',
            'highLowFlag',
            'normalRange',
            'provider.description',
            'provider.location.description',
            'result',
            'source.description',
            'test.description',
            'testDate'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadLabResultsByMember(this.memberId, options, includes, fields);
            this.memberLabs = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
